/**
 * Logo resolution for a Tradovate connection's prop firm.
 *
 * Order: explicit logoUrl (admin-editable, covers custom firms) → bundled
 * local file matched by slug → null (caller renders the color dot instead).
 * A connection with no prop firm linked gets the generic Tradovate logo.
 */

export const GENERIC_TRADOVATE_LOGO = "/logos/tradovate.png";

// Logos bundled in app/public/logos/propfirms/. Keys are PropFirmTemplate slugs.
export const LOCAL_PROPFIRM_LOGOS: Record<string, string> = {
  apex: "/logos/propfirms/apex.png",
  bulenox: "/logos/propfirms/bulenox.png",
  fundednext: "/logos/propfirms/fundednext.png",
  lucid: "/logos/propfirms/lucid.png",
  myfundedfutures: "/logos/propfirms/myfundedfutures.png",
  takeprofittrader: "/logos/propfirms/takeprofittrader.png",
  topstep: "/logos/propfirms/topstep.png",
  tradeify: "/logos/propfirms/tradeify.png",
};

export interface PropFirmLogoInput {
  slug: string;
  logoUrl: string | null;
  color: string;
}

export interface ResolvedPropFirmLogo {
  /** Image to render, or null when the caller should render a color dot. */
  src: string | null;
  /** Badge color for the dot fallback; null when no prop firm is linked. */
  color: string | null;
}

export function resolvePropFirmLogo(firm: PropFirmLogoInput | null): ResolvedPropFirmLogo {
  if (!firm) return { src: GENERIC_TRADOVATE_LOGO, color: null };

  const explicit = firm.logoUrl?.trim();
  if (explicit) return { src: explicit, color: firm.color };

  const local = LOCAL_PROPFIRM_LOGOS[firm.slug.toLowerCase()] ?? null;
  return { src: local, color: firm.color };
}
